import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import {
  useGetAllInvestmentsQuery,
  useApproveRejectInvestmentMutation,
  useDeleteInvestmentMutation,
  useBulkDeleteInvestmentsMutation,
  useGetAllProjectsQuery,
  Investment
} from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { Table } from '../../components/ui/Table';
import toast from 'react-hot-toast';
import { PlusIcon, CheckCircle, XCircle, Clock, Eye, Filter, DollarSign, TrendingUp, Search, RotateCcw, Target, Calendar as CalendarIcon, Trash2 } from 'lucide-react';
import UserAvatar from '../../components/common/UserAvatar';

// Investment type options
const investmentTypeOptions = [
  { value: 'equity', label: 'Equity' },
  { value: 'mutual_fund', label: 'Mutual Fund' },
  { value: 'fixed_deposit', label: 'Fixed Deposit' },
  { value: 'bonds', label: 'Bonds' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'crypto', label: 'Cryptocurrency' },
  { value: 'gold', label: 'Gold' },
  { value: 'other', label: 'Other' },
];

// Investment status options
const investmentStatusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const AdminInvestments: React.FC = () => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [selectedInvestment, setSelectedInvestment] = useState<Investment | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [investmentToDelete, setInvestmentToDelete] = useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedInvestmentIds, setSelectedInvestmentIds] = useState<number[]>([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);

  // API hooks
  const { data: investmentsData, isLoading, error, refetch } = useGetAllInvestmentsQuery();
  const { data: projectsResponse } = useGetAllProjectsQuery({});
  const [approveRejectInvestment] = useApproveRejectInvestmentMutation();
  const [deleteInvestment, { isLoading: isDeleting }] = useDeleteInvestmentMutation();
  const [bulkDeleteInvestments, { isLoading: isBulkDeleting }] = useBulkDeleteInvestmentsMutation();

  const investments = investmentsData?.data || [];
  const projects = projectsResponse?.data || [];

  const handleResetFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setProjectFilter('all');
  };

  const hasActiveFilters = searchTerm !== '' || filterStatus !== 'all' || projectFilter !== 'all';

  const filteredInvestments = investments.filter(investment => {
    const statusMatch = filterStatus === 'all' || investment.status === filterStatus;
    
    const searchMatch = !searchTerm || 
      (investment.user?.name && investment.user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (investment.user?.email && investment.user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (investment.project?.title && investment.project.title.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const projectMatch = projectFilter === 'all' || investment.project?.project_id.toString() === projectFilter;

    return statusMatch && searchMatch && projectMatch;
  });

  const projectOptions = [
    { value: 'all', label: 'All Projects' },
    ...projects.map(p => ({ value: p.project_id.toString(), label: p.title }))
  ];

  console.log(selectedInvestment, "__");


  const formatCurrency = (amount: string | number | null | undefined, currencySymbol: string = '₦') => {
    const value = parseFloat(String(amount || '0')) || 0;
    return `${currencySymbol}${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-700 bg-green-100 border border-green-200';
      case 'pending': return 'text-amber-700 bg-amber-100 border border-amber-200';
      case 'rejected': return 'text-red-700 bg-red-100 border border-red-200';
      default: return 'text-gray-700 bg-gray-100 border border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle className="h-4 w-4" />;
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'rejected': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const handleStatusUpdate = async (investment: any, status: 'approved' | 'rejected') => {
    try {
      await approveRejectInvestment({
        project_id: investment.project.project_id,
        investment_id: investment.investment_id,
        status,
      }).unwrap();

      toast.success(`Investment ${status} successfully!`);
      setShowModal(false);
      setSelectedInvestment(null);
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || `Failed to ${status} investment`);
    }
  };

  const handleViewDetails = (investment: any) => {
    setSelectedInvestment(investment);
    setShowModal(true);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedInvestmentIds(filteredInvestments.map(i => i.investment_id));
    } else {
      setSelectedInvestmentIds([]);
    }
  };

  const handleSelectOne = (id: number) => {
    if (selectedInvestmentIds.includes(id)) {
      setSelectedInvestmentIds(selectedInvestmentIds.filter(iId => iId !== id));
    } else {
      setSelectedInvestmentIds([...selectedInvestmentIds, id]);
    }
  };

  const handleDeleteClick = (investment: any) => {
    setInvestmentToDelete(investment);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteInvestment = async () => {
    if (!investmentToDelete) return;
    try {
      await deleteInvestment({ investment_id: investmentToDelete.investment_id }).unwrap();
      toast.success('Investment deleted successfully!');
      setIsDeleteModalOpen(false);
      setInvestmentToDelete(null);
      setSelectedInvestmentIds(prev => prev.filter(id => id !== investmentToDelete.investment_id));
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to delete investment');
    }
  };

  const confirmBulkDeleteInvestment = async () => {
    if (selectedInvestmentIds.length === 0) return;
    try {
      await bulkDeleteInvestments({ investment_ids: selectedInvestmentIds }).unwrap();
      toast.success(`Successfully deleted ${selectedInvestmentIds.length} investments!`);
      setIsBulkDeleteModalOpen(false);
      setSelectedInvestmentIds([]);
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to bulk delete investments');
    }
  };

  // Table columns configuration
  const columns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
          checked={filteredInvestments.length > 0 && selectedInvestmentIds.length === filteredInvestments.length}
          onChange={handleSelectAll}
        />
      ),
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
          checked={selectedInvestmentIds.includes(row.investment_id)}
          onChange={() => handleSelectOne(row.investment_id)}
        />
      ),
    },
    {
      key: 'sno',
      label: 'S.No',
      render: (_: any, __: any, index: number) => (
        <span className="font-bold text-gray-400 text-xs">#{index + 1}</span>
      ),
    },
    {
      key: 'investor',
      label: 'Investor',
      render: (value: any, row: any) => (
        <div className="flex items-center max-w-[200px] sm:max-w-[240px] min-w-0">
          <UserAvatar 
            src={row?.user?.profile_image} 
            name={row?.user?.name} 
            size="sm" 
            className="mr-2 md:mr-3 shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900 text-sm md:text-base truncate" title={row.user?.name}>{row.user?.name || 'Unknown User'}</p>
            <p className="text-xs md:text-sm text-gray-500 truncate" title={row.user?.email}>{row.user?.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'project',
      label: 'Project',
      render: (value: any, row: any) => {
        const title = row.project?.title || 'Unknown Project';
        const displayTitle = title.length > 45 ? `${title.substring(0, 45)}...` : title;
        return (
          <div className="max-w-[260px] sm:max-w-[320px] min-w-0">
            <p 
              className="font-medium text-gray-900 text-sm md:text-base truncate" 
              title={title}
            >
              {displayTitle}
            </p>
            <p className="text-xs md:text-sm text-gray-500 truncate" title={row.project?.location}>{row.project?.location || 'N/A'}</p>
          </div>
        );
      },
    },
    {
      key: 'amount_invested',
      label: 'Amount',
      render: (value: any, row: any) => (
        <p className="font-bold text-gray-900 text-sm md:text-base">
          {formatCurrency(value, row.currency_symbol || row.project?.currency_symbol)}
        </p>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: any) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(value)}`}>
          {getStatusIcon(value)}
          <span className="ml-1 hidden sm:inline">{value}</span>
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Date',
      render: (value: any) => (
        <p className="text-xs md:text-sm text-gray-900">
          {formatDateUtil(value)}
        </p>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value: any, row: any) => (
        <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewDetails(row)}
            className="text-xs px-2 py-1 text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200 hover:border-green-300 hover:shadow-sm"
          >
            <Eye className="h-3 w-3 md:h-4 md:w-4 mr-1" />
            <span className="hidden sm:inline">View</span>
          </Button>
          {row.status === 'pending' && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleStatusUpdate(row, 'approved')}
                className="text-xs px-2 py-1 text-green-600 border-green-600 hover:bg-green-50"
              >
                <CheckCircle className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                <span className="hidden sm:inline">Approve</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleStatusUpdate(row, 'rejected')}
                className="text-xs px-2 py-1 text-red-600 border-red-600 hover:bg-red-50"
              >
                <XCircle className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                <span className="hidden sm:inline">Reject</span>
              </Button>
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDeleteClick(row)}
            disabled={isDeleting}
            className="text-xs px-2 py-1 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300 hover:shadow-sm"
          >
            <Trash2 className="h-3 w-3 md:h-4 md:w-4 mr-1" />
            <span className="hidden sm:inline">Delete</span>
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Investment Management</h1>
          <p className="text-sm text-gray-600">Review and manage investment applications from users</p>
        </div>
      </div>

      {/* Premium Filtering Hub */}
      <Card className="p-6 mb-8 bg-white border-none shadow-xl rounded-2xl overflow-visible">
        <div className="flex flex-col space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            {/* Search Bar */}
            <div className="relative flex-1 group min-w-[300px]">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-amber-500 transition-colors" />
              <Input
                placeholder="Search by investor name, email, or project..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-14 bg-gray-50/50 border-gray-200 focus:bg-white transition-all shadow-inner rounded-2xl text-base"
              />
            </div>

            {/* Filter Actions */}
            <div className="flex items-center gap-4">
              {hasActiveFilters && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleResetFilters}
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 h-12 px-6 font-bold text-xs uppercase tracking-widest transition-all shadow-sm active:scale-95"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Clear All
                </Button>
              )}
            </div>
          </div>

          {/* Filter Selectors Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6 pt-2">
            <div className="space-y-2">
              <label className="flex items-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                <Clock className="w-3 h-3 mr-1.5" /> Approval Status
              </label>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'pending', label: 'Pending Review' },
                  { value: 'approved', label: 'Approved' },
                  { value: 'rejected', label: 'Rejected' },
                ]}
                className="h-12 text-sm font-semibold border-gray-200 bg-gray-50 focus:bg-white transition-all rounded-xl shadow-sm hover:border-amber-300"
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                <Target className="w-3 h-3 mr-1.5" /> Filter By Project
              </label>
              <Select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                options={projectOptions}
                className="h-12 text-sm font-semibold border-gray-200 bg-gray-50 focus:bg-white transition-all rounded-xl shadow-sm hover:border-amber-300"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Results Header */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em]">Investment Activity</h3>
          <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-100 font-bold px-3 py-1 rounded-full">
            {filteredInvestments.length} Applications
          </Badge>
        </div>
      </div>

      {/* Investments Table */}
      <Card className="p-0 border-none shadow-2xl overflow-hidden bg-white/50 backdrop-blur-sm">
        {filteredInvestments.length === 0 ? (
          <div className="text-center py-20 bg-white">
            <div className="text-gray-200 mb-6">
              <TrendingUp className="w-20 h-20 mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Records Matching Filters</h3>
            <p className="text-gray-500 mb-8 max-w-xs mx-auto">We couldn't find any investment records. Try adjusting your search criteria or clearing filters.</p>
            {hasActiveFilters && (
              <Button onClick={handleResetFilters} variant="outline" size="sm" className="font-bold border-gray-300">
                Clear All Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table
              columns={columns}
              data={filteredInvestments}
              itemsPerPage={10}
            />
          </div>
        )}
      </Card>

      {/* Investment Details Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Investment Details"
        size="xl"
      >
        {selectedInvestment && (
          <div className="space-y-4">
            {/* Investment Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3 text-sm">Investment Details</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-sm">Amount:</span>
                    <span className="font-medium text-sm">
                      {formatCurrency(selectedInvestment.amount_invested, selectedInvestment.currency_symbol || selectedInvestment.project?.currency_symbol)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-sm">Status:</span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(selectedInvestment.status)}`}>
                      {getStatusIcon(selectedInvestment.status)}
                      <span className="ml-1">{selectedInvestment.status}</span>
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-sm">Date:</span>
                    <span className="font-medium text-sm">{formatDateUtil(selectedInvestment.created_at)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3 text-sm">Project Details</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-gray-600 text-sm">Title:</span>
                    <p className="font-medium text-sm">{selectedInvestment.project.title}</p>
                  </div>
                  <div>
                    <span className="text-gray-600 text-sm">Location:</span>
                    <p className="font-medium text-sm">{selectedInvestment.project.location}</p>
                  </div>
                  <div>
                    <span className="text-gray-600 text-sm">Funding Goal:</span>
                    <p className="font-medium text-sm">
                      {formatCurrency(selectedInvestment.project.funding_goal.toString(), selectedInvestment.project.currency_symbol)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Investor Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm">Investor Information</h3>
              <div className="flex items-center space-x-4">
                <UserAvatar 
                  src={selectedInvestment.user.profile_image} 
                  name={selectedInvestment.user.name} 
                  size="lg" 
                />
                <div>
                  <p className="font-medium text-gray-900 text-sm">{selectedInvestment.user.name || 'Unknown User'}</p>
                  <p className="text-gray-600 text-sm">{selectedInvestment.user.email}</p>
                  <p className="text-xs text-gray-500">
                    {selectedInvestment.user.is_verified ? 'Verified User' : 'Unverified User'}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Proof */}
            {selectedInvestment?.payment_screenshot && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3 text-sm">Payment Proof</h3>
                <img
                  src={`${selectedInvestment.payment_screenshot}`}
                  alt="Payment proof"
                  className="max-w-full h-48 object-contain border border-gray-200 rounded-lg"
                />
              </div>
            )}

            {/* Action Buttons */}
            {selectedInvestment.status === 'pending' && (
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="w-full sm:w-auto text-sm px-4 py-2"
                >
                  Close
                </Button>
                <Button
                  onClick={() => handleStatusUpdate(selectedInvestment, 'approved')}
                  className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-sm px-4 py-2"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Approve Investment</span>
                  <span className="sm:hidden">Approve</span>
                </Button>
                <Button
                  onClick={() => handleStatusUpdate(selectedInvestment, 'rejected')}
                  className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-sm px-4 py-2"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Reject Investment</span>
                  <span className="sm:hidden">Reject</span>
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Investment Record"
      >
        <div className="space-y-4 p-2">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete the investment of{' '}
            <span className="font-bold text-gray-900">
              {formatCurrency(investmentToDelete?.amount_invested, investmentToDelete?.currency_symbol || investmentToDelete?.project?.currency_symbol)}
            </span>{' '}
            by <span className="font-bold text-gray-900">{investmentToDelete?.user?.name || 'User'}</span> for{' '}
            <span className="font-bold text-gray-900">{investmentToDelete?.project?.title}</span>?
          </p>
          <p className="text-xs text-red-500 font-semibold">
            This action cannot be undone. If the investment was approved, the project's raised amount will be automatically adjusted.
          </p>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteInvestment}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
            >
              {isDeleting ? 'Deleting...' : 'Delete Investment'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        isOpen={isBulkDeleteModalOpen}
        onClose={() => setIsBulkDeleteModalOpen(false)}
        title="Bulk Delete Investments"
      >
        <div className="space-y-4 p-2">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete <span className="font-bold text-gray-900">{selectedInvestmentIds.length}</span> selected investment(s)?
          </p>
          <p className="text-xs text-red-500 font-semibold">
            This action cannot be undone. For any approved investments in this list, the project raised amounts will be automatically adjusted.
          </p>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={() => setIsBulkDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmBulkDeleteInvestment}
              disabled={isBulkDeleting}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
            >
              {isBulkDeleting ? 'Deleting...' : `Delete ${selectedInvestmentIds.length} Investments`}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Floating Bulk Action Bar */}
      {selectedInvestmentIds.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50 bg-gray-900 text-white px-6 py-3.5 rounded-xl shadow-2xl flex items-center gap-4 border border-gray-700 transition-all">
          <span className="text-sm font-semibold">{selectedInvestmentIds.length} investment(s) selected</span>
          <button
            onClick={() => setIsBulkDeleteModalOpen(true)}
            className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Multi-Delete ({selectedInvestmentIds.length})
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminInvestments;